import React from 'react'
import { observer } from 'mobx-react'
import Button from 'components/Button'
import Icon from 'components/Icon'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetHeader,
  ModalSheetContentBlock,
  ModalSheetFooter,
  ModalSheetFooterSpacer,
  ModalSheetFooterButtonGroup,
  ModalSheetListItem,
  ModalSheetSpacer,
} from 'components/ModalSheet'
import ViewScopeChangesViewModel from './ViewJobScopeChangeRequestViewModel'
import { TextAreaContainer } from 'components/inputs'
import styles from './ViewJobScopeChangeRequestDialog.sass'

interface ViewJobScopeChangeRequestDialogProps {
  vm: ViewScopeChangesViewModel
}

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour12: true,
  hour: 'numeric',
  minute: '2-digit',
})

export default observer(function ViewJobScopeChangeRequestDialog({
  vm,
}: ViewJobScopeChangeRequestDialogProps) {
  return (
    <ModalSheet showing={vm.showing} onRequestClose={vm.close}>
      <ModalSheetHeader withCloseButton />
      <ModalSheetContent>
        <ModalSheetContentBlock>
          {vm.scopeChangeRequest.isAccepted && (
            <>
              <ModalSheetContentTitle>
                <div className={styles.header}>Acknowledgment captured</div>
              </ModalSheetContentTitle>

              <ModalSheetContentDescription>
                <div className={styles.header}>
                  {vm.scopeChangeRequest.isCreatedByAdmin
                    ? 'An admin updated the job scope on'
                    : 'A Customer Service Representative reviewed and accepted all job updates identified by you on'}{' '}
                  {dateFormatter.format(vm.scopeChangeRequest.createTime)}.
                </div>
              </ModalSheetContentDescription>
            </>
          )}

          {vm.scopeChangeRequest.isRejected && (
            <>
              <ModalSheetContentTitle>
                <div className={styles.header}>Acknowledgment captured</div>
              </ModalSheetContentTitle>

              <ModalSheetContentDescription>
                <div className={styles.header}>
                  A Customer Service Representative reviewed and rejected all
                  job updates identified by{' '}
                  {vm.scopeChangeRequest.isCreatedByAdmin
                    ? 'an admin on'
                    : 'you on'}{' '}
                  {dateFormatter.format(vm.scopeChangeRequest.createTime)}.
                </div>
              </ModalSheetContentDescription>
            </>
          )}

          {vm.scopeChangeRequest.isPending && (
            <>
              <ModalSheetContentTitle>
                <div className={styles.header}>Pending review</div>
              </ModalSheetContentTitle>

              <ModalSheetContentDescription>
                <div className={styles.header}>
                  {vm.scopeChangeRequest.isCreatedByAdmin
                    ? 'An admin has'
                    : 'You have'}{' '}
                  identified services that fall outside the original job scope.
                  Pending review from a Customer Service Representative.
                </div>
              </ModalSheetContentDescription>
            </>
          )}
        </ModalSheetContentBlock>

        <ModalSheetListItem>
          <div className={styles.listItem}>
            <span>
              {vm.scopeChangeRequest.isAccepted &&
                `Accepted services (${vm.scopeChangeRequest.items.length})`}
              {vm.scopeChangeRequest.isRejected &&
                `Declined services (${vm.scopeChangeRequest.items.length})`}
              {vm.scopeChangeRequest.isPending &&
                `Services (${vm.scopeChangeRequest.items.length})`}
            </span>
            <span>Total</span>
          </div>
        </ModalSheetListItem>
        <div className={styles.itemsContainer}>
          {vm.scopeChangeRequest.items.map((item) => (
            <ModalSheetListItem key={item.id}>
              <div className={styles.listItem}>
                <span>
                  {vm.scopeChangeRequest.status === 'Accepted' && (
                    <Icon
                      className={styles.listItemIcon}
                      name="checkmark"
                      color="var(--green400)"
                      size={16}
                    />
                  )}

                  {vm.scopeChangeRequest.status === 'Rejected' && (
                    <Icon
                      className={styles.listItemIcon}
                      name="declined"
                      color="var(--red400)"
                      size={16}
                    />
                  )}
                  <strong>{item.name}</strong>
                </span>
                <span>
                  <span>{item.quantity}</span>
                </span>
              </div>
            </ModalSheetListItem>
          ))}
        </div>
        <ModalSheetSpacer />
        {vm.scopeChangeRequest.isRejected && (
          <ModalSheetContentBlock>
            <div className={styles.reasonContainer}>
              <label className={styles.reasonLabel}>Reason</label>
              <TextAreaContainer
                rows={4}
                disabled
                model={vm.scopeChangeRequest}
                field="rejectionReason"
                name="rejectionReason"
              />
            </div>
          </ModalSheetContentBlock>
        )}
      </ModalSheetContent>

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button onClick={vm.close}>Close</Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})
